import type { MultilineContent } from "@repo/app-types/content/types";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
  PlaybookNode,
  PlaybookNodeId,
  PlaybookSectionNode,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import {
  computeNodeChangeStatuses,
  getPlaybookNode,
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import {
  collectGlobalOnlyChanges,
  getPublishNodeDisclosure,
} from "@repo/app-types/helpers/playbookNodePublish";
import {
  addCriteria,
  addSection,
  findCriteria,
  findSection,
  moveCriteriaAfter,
  moveCriteriaBefore,
  moveCriteriaToSection,
  moveSectionAfter,
  moveSectionBefore,
  removeCriteria,
  removeSection,
  updateCriteria,
  updateSection,
} from "@repo/app-types/helpers/playbookTreeOperations";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconFileText, IconPlus, IconStar } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import type { DragItem } from "react-aria";
import {
  Tree,
  TreeItem,
  TreeItemContent,
  useDragAndDrop,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";
import {
  findCriterionProposalForNode,
  findOutcomeProposal,
  findProposalForNode,
  findRemovalProposalForNode,
  findSectionProposalForNode,
  getProposedCriteriaForSection,
  getProposedSectionsForParent,
  type SampleToolResultCriterionEntry,
  type SampleToolResultSectionEntry,
  type TestResultSelection,
  type UseSampleToolResultsResult,
} from "../hooks/useSampleToolResults.js";
import { AssessmentCriteriaTreeItem } from "./recordingAssessmentEditor/assessmentCriteriaTreeItem.js";
import { AssessmentEditorSplitLayout } from "./recordingAssessmentEditor/assessmentEditorSplitLayout.js";
import { AssessmentSectionTreeItem } from "./recordingAssessmentEditor/assessmentSectionTreeItem.js";
import {
  deserializeAssessmentTreeItemKey,
  resolveInitialAssessmentTreeItemKey,
  serializeAssessmentTreeItemKey,
  useAssessmentTreeExpandAndCollapse,
  type AssessmentTreeItemKey,
} from "./recordingAssessmentEditor/assessmentTreeHelpers.js";
import {
  buildNewCriterionFromSuggestion,
  CriteriaSettings,
  isCriterionInSuggestionMode,
} from "./recordingAssessmentEditor/criteriaSettings.js";
import { NodeChangeDot } from "./recordingAssessmentEditor/nodeChangeDot.js";
import {
  isOutcomeInSuggestionMode,
  OutcomeSettingsForm,
} from "./recordingAssessmentEditor/outcomeSettingsForm.js";
import { PublishSettingButton } from "./recordingAssessmentEditor/publishSettingButton.js";
import {
  buildNewSectionFromSuggestion,
  isSectionInSuggestionMode,
  RecordingAssessmentSectionSettings,
} from "./recordingAssessmentEditor/recordingAssessmentSectionSettings.js";
import { SummaryTemplateSettingsForm } from "./recordingAssessmentEditor/summaryTemplateSettingsForm.js";
import { useAssessmentTreeSelection } from "./recordingAssessmentEditor/useAssessmentTreeSelection.js";
import { addActionItemContent, treeItem } from "./tree/treeStyles.js";

export interface RecordingAssessmentEditorProps {
  tree: PlaybookTree;
  /** The live/published tree for change comparison (used to show edit indicators) */
  liveTree?: PlaybookTree;
  onChange: (template: PlaybookTree) => void;
  isReadOnly?: boolean;
  outcomeHandler: {
    outcome: OutcomeDefinition | null;
    initialOutcome: OutcomeDefinition | null;
    /**
     * Live/published outcome, for the draft-change dot and the context diff
     * toggle. `undefined` when the live revision hasn't loaded yet (no dot).
     */
    liveOutcome?: OutcomeDefinition | null;
    onChange: (outcomeDefinition: OutcomeDefinition | null) => void;
    onPublish?: () => void;
    isPublishing?: boolean;
  };
  summaryTemplateHandler: {
    body: MultilineContent | null;
    liveBody?: MultilineContent | null;
    onChange: (body: MultilineContent | null) => void;
    onPublish?: () => void;
    isPublishing?: boolean;
  };
  /** Initial URL-backed assessment-tree selection. */
  initialSelectedKey?: AssessmentTreeItemKey;
  onOptimizeCriterion?: (criteria: PlaybookCriterionNode) => void;
  onTestCriterion?: (criteria: PlaybookCriterionNode) => void;
  onOptimizeSection?: (section: PlaybookSectionNode) => void;
  onTestSection?: (section: PlaybookSectionNode) => void;
  onOptimizeOutcome?: (outcome: OutcomeDefinition) => void;
  onTestOutcome?: (outcome: OutcomeDefinition) => void;
  isCopilotBusy?: boolean;

  /** Absent where inline publishing isn't offered (new playbooks, the agent's playbook tab). */
  onPublishNode?: (nodeId: string) => void;
  publishingNodeId?: string | null;
  /** Inline publish buttons wait while any publish or discard is in flight. */
  isPublishBlocked?: boolean;

  /**
   * Sample-tool results collected during this editing session, keyed by
   * `proposal_id`. The criteria settings pane looks up an entry
   * by the selected criterion's id to render the agent's suggestion.
   */
  sampleToolResults?: UseSampleToolResultsResult["results"];

  /** Drop the saved sample-tool result for the given criterion id. */
  onResolveSampleToolResultForNode?: (
    playbookNodeId: PlaybookNodeId,
    status: "applied" | "discarded",
  ) => void;
  playbookId?: PlaybookId | null;
  /** Restrict outcome editing to transcript-derived outcomes (voice agents). */
  isTranscriptOutcomeOnly?: boolean;

  /**
   * Imperative selection signal: each time the object identity changes (the
   * caller bumps `nonce`), select the referenced criterion. Used by the
   * assistant sidebar's suggestion list to navigate the editor without
   * coupling its selection state to a URL param. `kind === "create_criterion"`
   * selects the ghost tree key (the id won't exist in the live tree);
   * otherwise the existing-criteria path is used.
   */
  criterionSelectionRequest?: {
    id: PlaybookNodeId;
    selection: TestResultSelection;
    nonce: number;
  } | null;
  /**
   * Reports the currently selected tree key whenever it changes, so a sibling
   * (the assistant's suggestion list) can flag the row that's being viewed.
   */
  onCurrentSelectionChange?: (key: AssessmentTreeItemKey | undefined) => void;
}

function getLiveSection(
  liveTree: PlaybookTree | undefined,
  sectionId: string,
): PlaybookSectionNode | undefined {
  if (!liveTree) return undefined;
  const node = getPlaybookNode(sectionId, liveTree);
  return node && isPlaybookSectionNode(node) ? node : undefined;
}

function getLiveCriteria(
  liveTree: PlaybookTree | undefined,
  criteriaId: string,
): PlaybookCriterionNode | undefined {
  if (!liveTree) return undefined;
  const node = getPlaybookNode(criteriaId, liveTree);
  return node && isPlaybookCriterionNode(node) ? node : undefined;
}

function useAssessmentTreeDragAndDrop(
  template: PlaybookTree,
  setTemplate: (newTemplate: PlaybookTree) => void,
) {
  return useDragAndDrop({
    acceptedDragTypes: [
      "application/vnd.alpharun.assessment-section",
      "application/vnd.alpharun.assessment-criteria",
    ],

    getItems: (keys) =>
      Array.from(keys)
        .map((key): DragItem | undefined => {
          const treeItemKey = deserializeAssessmentTreeItemKey(key);
          switch (treeItemKey?.type) {
            case "section":
              return {
                "application/vnd.alpharun.assessment-section": treeItemKey.id,
              };

            case "criteria":
              return {
                "application/vnd.alpharun.assessment-criteria": treeItemKey.id,
              };

            default:
              return undefined;
          }
        })
        .filter(isNotUndefined),

    shouldAcceptItemDrop: (target, types) => {
      const treeItemKey = deserializeAssessmentTreeItemKey(target.key);
      switch (treeItemKey?.type) {
        case "section":
          switch (target.dropPosition) {
            case "on":
            case "after":
              return (
                types.has("application/vnd.alpharun.assessment-criteria") ||
                types.has("application/vnd.alpharun.assessment-section")
              );

            case "before":
              return types.has("application/vnd.alpharun.assessment-section");

            default:
              return false;
          }

        case "criteria":
          switch (target.dropPosition) {
            case "before":
            case "after":
              return types.has("application/vnd.alpharun.assessment-criteria");

            default:
              return false;
          }

        default:
          return false;
      }
    },

    onMove: ({ target, keys }) => {
      const targetTreeItemKey = deserializeAssessmentTreeItemKey(target.key);
      const sourceTreeItemKey = Array.from(keys)
        .map(deserializeAssessmentTreeItemKey)
        .at(0);

      if (!targetTreeItemKey || !sourceTreeItemKey) {
        return;
      }

      // Handle different drag and drop scenarios using the clean utility functions
      if (
        sourceTreeItemKey.type === "section" &&
        target.dropPosition === "before" &&
        targetTreeItemKey.type === "section"
      ) {
        setTemplate(
          moveSectionBefore(
            template,
            sourceTreeItemKey.id,
            targetTreeItemKey.id,
          ),
        );
      } else if (
        sourceTreeItemKey.type === "section" &&
        target.dropPosition === "after" &&
        targetTreeItemKey.type === "section"
      ) {
        setTemplate(
          moveSectionAfter(
            template,
            sourceTreeItemKey.id,
            targetTreeItemKey.id,
          ),
        );
      } else if (
        sourceTreeItemKey.type === "criteria" &&
        (target.dropPosition === "on" || target.dropPosition === "after") &&
        targetTreeItemKey.type === "section"
      ) {
        setTemplate(
          moveCriteriaToSection(
            template,
            sourceTreeItemKey.id,
            targetTreeItemKey.id,
          ),
        );
      } else if (
        sourceTreeItemKey.type === "criteria" &&
        target.dropPosition === "before" &&
        targetTreeItemKey.type === "criteria"
      ) {
        setTemplate(
          moveCriteriaBefore(
            template,
            sourceTreeItemKey.id,
            targetTreeItemKey.id,
          ),
        );
      } else if (
        sourceTreeItemKey.type === "criteria" &&
        target.dropPosition === "after" &&
        targetTreeItemKey.type === "criteria"
      ) {
        setTemplate(
          moveCriteriaAfter(
            template,
            sourceTreeItemKey.id,
            targetTreeItemKey.id,
          ),
        );
      }
    },
  });
}

export const RecordingAssessmentEditor: FC<RecordingAssessmentEditorProps> = ({
  tree: template,
  liveTree,
  onChange,
  isReadOnly = false,
  outcomeHandler,
  summaryTemplateHandler,
  initialSelectedKey,
  onOptimizeCriterion,
  onTestCriterion,
  onOptimizeSection,
  onTestSection,
  onOptimizeOutcome,
  onTestOutcome,
  isCopilotBusy = false,
  onPublishNode,
  publishingNodeId,
  isPublishBlocked = false,
  sampleToolResults,
  onResolveSampleToolResultForNode,
  playbookId,
  isTranscriptOutcomeOnly = false,
  criterionSelectionRequest,
  onCurrentSelectionChange,
}) => {
  // Compute change statuses for all nodes (comparing current to live)
  const nodeChangeStatuses = useMemo(
    () => computeNodeChangeStatuses(liveTree, template),
    [liveTree, template],
  );

  const globalOnlyChanges = useMemo(
    () => (liveTree ? collectGlobalOnlyChanges(liveTree, template) : []),
    [liveTree, template],
  );

  const { expandedKeys, onExpandedChange } =
    useAssessmentTreeExpandAndCollapse(template);

  const [resolvedInitialSelectedKey] = useState<AssessmentTreeItemKey>(() =>
    resolveInitialAssessmentTreeItemKey(template, initialSelectedKey),
  );

  const {
    selectedKey,
    selectedKeys,
    onSelectionChange,
    selectedSection,
    selectedCriteria,
    selectedOutcome,
    selectedSummaryTemplate,
    selectedGhostCriterionId,
    selectedGhostSectionId,
    setSelectedKey,
  } = useAssessmentTreeSelection({
    initialSelectedKey: resolvedInitialSelectedKey,
    template,
  });

  // Report the current selection up so the assistant's suggestion list can flag
  // the row that's being viewed. Fires for both tree clicks and suggestion-row
  // selections (both funnel through `selectedKey`).
  useEffect(() => {
    onCurrentSelectionChange?.(selectedKey);
  }, [selectedKey, onCurrentSelectionChange]);

  const selectedGhostCriterion = selectedGhostCriterionId
    ? findCriterionProposalForNode(sampleToolResults, selectedGhostCriterionId)
    : undefined;

  const selectedGhostSection = selectedGhostSectionId
    ? findSectionProposalForNode(sampleToolResults, selectedGhostSectionId)
    : undefined;

  const isCriteriaSuggestionMode =
    selectedGhostCriterion !== undefined ||
    (selectedCriteria !== undefined &&
      isCriterionInSuggestionMode(
        selectedCriteria,
        findCriterionProposalForNode(sampleToolResults, selectedCriteria.id),
      ));

  const isSectionSuggestionMode =
    selectedGhostSection !== undefined ||
    (selectedSection !== undefined &&
      isSectionInSuggestionMode(
        selectedSection,
        findSectionProposalForNode(sampleToolResults, selectedSection.id),
      ));

  const outcomeSuggestion = findOutcomeProposal(sampleToolResults);
  // Whether the agent has an applyable outcome rubric suggestion. Drives the
  // tree suggestion dot (not gated on selection) and the suggestion-mode pane.
  const hasOutcomeSuggestion =
    outcomeHandler.outcome !== null &&
    isOutcomeInSuggestionMode(outcomeHandler.outcome, outcomeSuggestion);
  const isOutcomeSuggestionMode =
    selectedOutcome !== undefined && hasOutcomeSuggestion;
  // True when any settings panel is rendering an agent suggestion. Drives the
  // right pane's indigo border and the switch to internal (results-only)
  // scrolling.
  const isAnySuggestionMode =
    isCriteriaSuggestionMode ||
    isOutcomeSuggestionMode ||
    isSectionSuggestionMode;
  // Draft-change dot: the working outcome differs from the published one.
  // `undefined` liveOutcome means the live revision hasn't loaded — no dot,
  // mirroring how `computeNodeChangeStatuses` yields nothing without a live tree.
  const isOutcomeChanged =
    outcomeHandler.liveOutcome !== undefined &&
    JSON.stringify(outcomeHandler.outcome) !==
      JSON.stringify(outcomeHandler.liveOutcome);
  const isSummaryTemplateChanged =
    summaryTemplateHandler.liveBody !== undefined &&
    JSON.stringify(summaryTemplateHandler.body) !==
      JSON.stringify(summaryTemplateHandler.liveBody);

  // React to selection requests from the assistant sidebar. `create_criterion`
  // proposals live only in sampleToolResults (their id intentionally isn't
  // in the playbook tree), so they're routed through the ghost selection
  // key. The ref-based nonce gate ensures we only act when the caller
  // actually bumps the nonce — without it, unrelated `template` changes
  // (e.g. applying a ghost suggestion mutates the tree) would re-fire the
  // effect and clobber the post-apply selection that `applyGhostCriterion`
  // just installed. Mirrors the request-id pattern in audioPlayer/provider.tsx.
  const lastHandledSelectionNonceRef = useRef(0);
  useEffect(() => {
    if (!criterionSelectionRequest) return;
    if (
      criterionSelectionRequest.nonce <= lastHandledSelectionNonceRef.current
    ) {
      return;
    }
    lastHandledSelectionNonceRef.current = criterionSelectionRequest.nonce;
    const { id, selection } = criterionSelectionRequest;
    if (selection === "ghost-criterion" || selection === "ghost-section") {
      setSelectedKey({ type: selection, id });
      return;
    }
    if (selection === "outcome") {
      // A playbook has a single outcome pane; the synthetic id is ignored.
      setSelectedKey({ type: "outcome", id: "outcome" });
      return;
    }
    if (selection === "section") {
      if (findSection(template, id) === undefined) {
        return;
      }
      setSelectedKey({ type: "section", id });
      return;
    }
    if (findCriteria(template, id) === undefined) return;
    setSelectedKey({ type: "criteria", id });
  }, [criterionSelectionRequest, template, setSelectedKey]);

  const { dragAndDropHooks } = useAssessmentTreeDragAndDrop(template, onChange);

  const onSectionCreateClick = (parentSectionId?: string) => {
    const newSection: PlaybookSectionNode = {
      type: "section",
      id: uuidv4(),
      name: "",
      description: "",
      nodes: [],
    };

    onChange(addSection(template, newSection, parentSectionId));
    setSelectedKey({ type: "section", id: newSection.id });
  };

  const onCriteriaCreateClick = (sectionId: string) => {
    const newCriteria: PlaybookCriterionNode = {
      type: "criteria",
      id: uuidv4(),
      name: "",
      description: "",
      grading_context: "",
      grading_value_type: "1_5_score",
    };

    onChange(addCriteria(template, newCriteria, sectionId));
    setSelectedKey({ type: "criteria", id: newCriteria.id });
  };

  const handleUpdateCriteria = (updatedCriteria: PlaybookCriterionNode) => {
    onChange(updateCriteria(template, updatedCriteria));
  };

  const handleUpdateSection = (updatedSection: PlaybookSectionNode) => {
    onChange(updateSection(template, updatedSection));
  };

  const deleteCriteria = (criteriaId: string) => {
    const [newTemplate] = removeCriteria(template, criteriaId);
    onChange(newTemplate);
    setSelectedKey({ type: "outcome", id: "outcome" });
  };

  const deleteSection = (sectionId: string) => {
    const [newTemplate] = removeSection(template, sectionId);
    onChange(newTemplate);
    setSelectedKey({ type: "outcome", id: "outcome" });
  };

  /** Apply a proposal's edit to the tree, then record it as applied. */
  const applyProposal = (nodeId: string, editTree: () => void) => {
    editTree();
    onResolveSampleToolResultForNode?.(nodeId, "applied");
  };

  /** Reject a proposal, leaving the tree as it is. */
  const discardProposal = (nodeId: string) => {
    onResolveSampleToolResultForNode?.(nodeId, "discarded");
  };

  const applyGhostCriterion = (
    ghost: SampleToolResultCriterionEntry,
    mergedCriterion: PlaybookCriterionNode,
  ) => {
    const parentSectionId = ghost.update?.parent_section_id;
    if (!parentSectionId) return;
    applyProposal(ghost.playbook_node_id, () => {
      onChange(addCriteria(template, mergedCriterion, parentSectionId));
    });
    setSelectedKey({ type: "criteria", id: mergedCriterion.id });
  };

  const discardGhostCriterion = (ghost: SampleToolResultCriterionEntry) => {
    discardProposal(ghost.playbook_node_id);
    setSelectedKey({ type: "outcome", id: "outcome" });
  };

  const applyGhostSection = (
    ghost: SampleToolResultSectionEntry,
    mergedSection: PlaybookSectionNode,
  ) => {
    applyProposal(ghost.playbook_node_id, () => {
      onChange(
        addSection(
          template,
          mergedSection,
          ghost.update?.parent_section_id ?? undefined,
        ),
      );
    });
    setSelectedKey({ type: "section", id: mergedSection.id });
  };

  const discardGhostSection = (ghost: SampleToolResultSectionEntry) => {
    discardProposal(ghost.playbook_node_id);
    setSelectedKey({ type: "outcome", id: "outcome" });
  };

  // Tree styling for drop indicators
  const treeStyle = `
    @scope {
      .react-aria-DropIndicator {
        outline: 1px solid #9333ea;
      }
    }
  `;

  // Helper function to render nodes recursively
  const renderNodes = (
    nodes: PlaybookNode[],
    level: number = 0,
    isParentLastItemAtLevel: boolean = true,
  ) => {
    let sectionIndex = 0;
    return nodes.map((node, nodeIndex, { length: nodeCount }) => {
      if (isPlaybookSectionNode(node)) {
        const currentSectionIndex = sectionIndex++;
        const criteriaNodes = node.nodes.filter(isPlaybookCriterionNode);

        const isSectionLastItemAtLevel =
          level === 0
            ? // Every top-level section is in a group of its own, so every top-level section is the first and last item at its level.
              true
            : // All other sections are the last item at its level if the ancestors were the last item and this is the last child.
              isParentLastItemAtLevel && nodeIndex === nodeCount - 1;

        const ghostCriteriaForSection = getProposedCriteriaForSection(
          sampleToolResults,
          node.id,
        );
        const hasGhostChildren = ghostCriteriaForSection.length > 0;

        return (
          <AssessmentSectionTreeItem
            key={node.id}
            section={node}
            sectionIndex={currentSectionIndex}
            level={level}
            isLastItemAtLevel={isSectionLastItemAtLevel}
            onAddCriteria={onCriteriaCreateClick}
            onAddSubSection={onSectionCreateClick}
            isReadOnly={isReadOnly}
            changeStatus={nodeChangeStatuses.get(node.id)}
            hasSuggestion={
              isSectionInSuggestionMode(
                node,
                findSectionProposalForNode(sampleToolResults, node.id),
              ) ||
              findRemovalProposalForNode(sampleToolResults, node.id) !==
                undefined
            }
          >
            {/* Render all child nodes in their original order */}
            {node.nodes.map((childNode, childIndex, { length: childCount }) => {
              const isLastChildItemAtLevel =
                isSectionLastItemAtLevel &&
                childIndex === childCount - 1 &&
                !hasGhostChildren;

              if (isPlaybookSectionNode(childNode)) {
                return renderNodes(
                  [childNode],
                  level + 1,
                  isLastChildItemAtLevel,
                );
              } else if (isPlaybookCriterionNode(childNode)) {
                // Find the criteria index within all criteria for this section
                const criteriaIndex = criteriaNodes.findIndex(
                  (c) => c.id === childNode.id,
                );

                return (
                  <AssessmentCriteriaTreeItem
                    key={childNode.id}
                    criteria={childNode}
                    criteriaIndex={criteriaIndex}
                    level={level + 1}
                    isLastItemAtLevel={isLastChildItemAtLevel}
                    changeStatus={nodeChangeStatuses.get(childNode.id)}
                    hasSuggestion={
                      isCriterionInSuggestionMode(
                        childNode,
                        findCriterionProposalForNode(
                          sampleToolResults,
                          childNode.id,
                        ),
                      ) ||
                      findRemovalProposalForNode(
                        sampleToolResults,
                        childNode.id,
                      ) !== undefined
                    }
                  />
                );
              }
              return null;
            })}
            {ghostCriteriaForSection.map((ghost, ghostIndex) => (
              <AssessmentCriteriaTreeItem
                key={ghost.id}
                criteria={buildNewCriterionFromSuggestion(ghost)}
                criteriaIndex={0}
                isGhost
                hasSuggestion
                level={level + 1}
                isLastItemAtLevel={
                  isSectionLastItemAtLevel &&
                  ghostIndex === ghostCriteriaForSection.length - 1
                }
              />
            ))}
            {getProposedSectionsForParent(sampleToolResults, node.id).map(
              (ghost) => (
                <AssessmentSectionTreeItem
                  key={ghost.id}
                  section={buildNewSectionFromSuggestion(ghost)}
                  sectionIndex={0}
                  level={level + 1}
                  isGhost
                  hasSuggestion
                  isReadOnly={isReadOnly}
                />
              ),
            )}
          </AssessmentSectionTreeItem>
        );
      }
      return null;
    });
  };

  const renderOutcome = () => {
    return (
      <TreeItem
        id={serializeAssessmentTreeItemKey({ type: "outcome", id: "outcome" })}
        textValue="Outcome"
        className={treeItem({
          isLastItemAtLevel: true,
        })}
      >
        <TreeItemContent>
          <div className="flex min-h-14 items-center space-x-4 px-4 py-2">
            {/* Icon */}
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gray-100">
              <IconStar className="h-4 w-4 text-sidebar-foreground" />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              <div className="truncate leading-6 font-semibold text-foreground">
                Outcome
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4 text-xs">
              <NodeChangeDot
                entity="outcome"
                variant={hasOutcomeSuggestion ? "suggestion" : "draft"}
                isVisible={hasOutcomeSuggestion || isOutcomeChanged}
              />
            </div>
          </div>
        </TreeItemContent>
      </TreeItem>
    );
  };

  const renderSummaryTemplateItem = () => {
    return (
      <TreeItem
        id={serializeAssessmentTreeItemKey({
          type: "summary-template",
          id: "summary-template",
        })}
        textValue="Summary template"
        className={treeItem({
          isLastItemAtLevel: true,
        })}
      >
        <TreeItemContent>
          {/* min-h-14 matches the height of cards that have a subtitle line */}
          <div className="flex min-h-14 items-center space-x-4 px-4 py-2">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gray-100">
              <IconFileText className="h-4 w-4 text-sidebar-foreground" />
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="truncate leading-6 font-semibold text-foreground">
                Summary template
              </div>
            </div>
            <div className="flex items-center space-x-4 text-xs">
              <NodeChangeDot
                entity="summary template"
                isVisible={isSummaryTemplateChanged}
              />
            </div>
          </div>
        </TreeItemContent>
      </TreeItem>
    );
  };

  return (
    <AssessmentEditorSplitLayout
      sidebar={
        // Left pane - Tree
        <div className="relative min-h-0 min-w-0 flex-1 overflow-y-auto">
          <style hidden>{treeStyle}</style>

          <Tree
            aria-label="Assessment structure"
            selectionMode="single"
            selectionBehavior="replace"
            disallowEmptySelection
            selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            onExpandedChange={onExpandedChange}
            onSelectionChange={onSelectionChange}
            disabledBehavior="selection"
            dragAndDropHooks={dragAndDropHooks}
          >
            {renderOutcome()}
            {renderSummaryTemplateItem()}
            {renderNodes(template.nodes)}

            {/* Top-level `create_section` proposals from the agent */}
            {getProposedSectionsForParent(sampleToolResults, null).map(
              (ghost) => (
                <AssessmentSectionTreeItem
                  key={ghost.id}
                  section={buildNewSectionFromSuggestion(ghost)}
                  sectionIndex={0}
                  level={0}
                  isLastItemAtLevel
                  isGhost
                  hasSuggestion
                  isReadOnly={isReadOnly}
                />
              ),
            )}

            {/* Add top-level section button */}
            {!isReadOnly && (
              <TreeItem
                key="add-section"
                id={serializeAssessmentTreeItemKey({ type: "new-section" })}
                textValue="Add section"
                className={treeItem({
                  // This item is in a visual group of its own, so it's necessarily the first and last item.
                  isLastItemAtLevel: true,
                })}
                onAction={() => onSectionCreateClick()}
                isDisabled
              >
                <TreeItemContent>
                  <div className={addActionItemContent()}>
                    <IconPlus className="size-4" />
                    Add section
                  </div>
                </TreeItemContent>
              </TreeItem>
            )}
          </Tree>
        </div>
      }
    >
      {/* Right pane - Settings */}
      <div
        className={twMerge(
          "relative flex max-h-full min-h-0 flex-col rounded-lg border bg-gray-50/50 bg-white px-2 py-2",
          isAnySuggestionMode
            ? "overflow-y-auto md:overflow-hidden"
            : "overflow-y-auto",
          isAnySuggestionMode ? "border-indigo-500" : "border-border-light",
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col">
            {selectedOutcome ? (
              <OutcomeSettingsForm
                outcome={outcomeHandler.outcome}
                initialOutcome={outcomeHandler.initialOutcome}
                liveOutcome={outcomeHandler.liveOutcome ?? null}
                onUpdateOutcome={(updatedOutcome) => {
                  outcomeHandler.onChange(updatedOutcome);
                }}
                isReadOnly={isReadOnly}
                suggestion={outcomeSuggestion}
                onApplySuggestion={(mergedOutcome) => {
                  if (outcomeSuggestion) {
                    applyProposal(outcomeSuggestion.playbook_node_id, () => {
                      outcomeHandler.onChange(mergedOutcome);
                    });
                  }
                }}
                onDiscardSuggestion={
                  outcomeSuggestion
                    ? () => discardProposal(outcomeSuggestion.playbook_node_id)
                    : undefined
                }
                onOptimizeOutcome={onOptimizeOutcome}
                onTestOutcome={onTestOutcome}
                isCopilotBusy={isCopilotBusy}
                onPublishOutcome={outcomeHandler.onPublish}
                hasChangesToPublish={isOutcomeChanged}
                isPublishBlocked={isPublishBlocked}
                isPublishing={outcomeHandler.isPublishing}
                globalOnlyChanges={globalOnlyChanges}
                playbookId={playbookId}
                isTranscriptOnly={isTranscriptOutcomeOnly}
              />
            ) : null}
            {selectedSummaryTemplate ? (
              <SummaryTemplateSettingsForm
                body={summaryTemplateHandler.body}
                liveBody={summaryTemplateHandler.liveBody}
                onUpdateBody={summaryTemplateHandler.onChange}
                isReadOnly={isReadOnly}
                hasUnpublishedChanges={isSummaryTemplateChanged}
              />
            ) : null}
            {selectedSection ? (
              <RecordingAssessmentSectionSettings
                section={selectedSection}
                liveSection={getLiveSection(liveTree, selectedSection.id)}
                onUpdateSection={handleUpdateSection}
                isReadOnly={isReadOnly}
                proposal={findProposalForNode(
                  sampleToolResults,
                  selectedSection.id,
                )}
                onApplyProposal={(mergedSection) => {
                  applyProposal(selectedSection.id, () => {
                    if (mergedSection) {
                      handleUpdateSection(mergedSection);
                    } else {
                      deleteSection(selectedSection.id);
                    }
                  });
                }}
                onDiscardProposal={() => discardProposal(selectedSection.id)}
                onDelete={() => deleteSection(selectedSection.id)}
                onOptimizeSection={onOptimizeSection}
                onTestSection={onTestSection}
                isCopilotBusy={isCopilotBusy}
                onPublishNode={
                  onPublishNode
                    ? () => onPublishNode(selectedSection.id)
                    : undefined
                }
                publishDisclosure={
                  liveTree
                    ? getPublishNodeDisclosure(
                        liveTree,
                        template,
                        selectedSection.id,
                      )
                    : undefined
                }
                hasChangesToPublish={
                  nodeChangeStatuses.get(selectedSection.id) !== undefined
                }
                isPublishBlocked={isPublishBlocked}
                isPublishing={publishingNodeId === selectedSection.id}
                playbookId={playbookId}
              />
            ) : null}
            {selectedCriteria ? (
              <CriteriaSettings
                criteria={selectedCriteria}
                liveCriteria={getLiveCriteria(liveTree, selectedCriteria.id)}
                onUpdateCriterion={handleUpdateCriteria}
                isReadOnly={isReadOnly}
                proposal={findProposalForNode(
                  sampleToolResults,
                  selectedCriteria.id,
                )}
                outcomeDefinition={outcomeHandler.outcome}
                onApplyProposal={(mergedCriterion) => {
                  applyProposal(selectedCriteria.id, () => {
                    if (mergedCriterion) {
                      handleUpdateCriteria(mergedCriterion);
                    } else {
                      deleteCriteria(selectedCriteria.id);
                    }
                  });
                }}
                onDiscardProposal={() => discardProposal(selectedCriteria.id)}
                onDelete={() => deleteCriteria(selectedCriteria.id)}
                onOptimizeCriterion={onOptimizeCriterion}
                onTestCriterion={onTestCriterion}
                isCopilotBusy={isCopilotBusy}
                onPublishNode={
                  onPublishNode
                    ? () => onPublishNode(selectedCriteria.id)
                    : undefined
                }
                publishDisclosure={
                  liveTree
                    ? getPublishNodeDisclosure(
                        liveTree,
                        template,
                        selectedCriteria.id,
                      )
                    : undefined
                }
                hasChangesToPublish={
                  nodeChangeStatuses.get(selectedCriteria.id) !== undefined
                }
                isPublishBlocked={isPublishBlocked}
                isPublishing={publishingNodeId === selectedCriteria.id}
                playbookId={playbookId}
              />
            ) : null}
            {selectedGhostCriterion && !isReadOnly ? (
              <CriteriaSettings
                criteria={buildNewCriterionFromSuggestion(
                  selectedGhostCriterion,
                )}
                proposal={selectedGhostCriterion}
                outcomeDefinition={outcomeHandler.outcome}
                onApplyProposal={(mergedCriterion) => {
                  // A ghost is always an edit proposal, so it always carries
                  // the merged criterion to insert.
                  if (mergedCriterion) {
                    applyGhostCriterion(
                      selectedGhostCriterion,
                      mergedCriterion,
                    );
                  }
                }}
                onDiscardProposal={() =>
                  discardGhostCriterion(selectedGhostCriterion)
                }
                playbookId={playbookId}
              />
            ) : null}
            {selectedGhostSection && !isReadOnly ? (
              <RecordingAssessmentSectionSettings
                section={buildNewSectionFromSuggestion(selectedGhostSection)}
                proposal={selectedGhostSection}
                onApplyProposal={(mergedSection) => {
                  // A ghost is always an edit proposal, so it always carries
                  // the merged section to insert.
                  if (mergedSection) {
                    applyGhostSection(selectedGhostSection, mergedSection);
                  }
                }}
                onDiscardProposal={() =>
                  discardGhostSection(selectedGhostSection)
                }
                playbookId={playbookId}
              />
            ) : null}
          </div>

          {selectedSummaryTemplate && !isReadOnly && (
            <div className="flex items-center justify-between px-2 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <Button
                  variant={ButtonVariant.Warning}
                  onClick={() => {
                    summaryTemplateHandler.onChange(null);
                  }}
                  size={Size.SMALL}
                >
                  Delete
                </Button>
              </div>
              {summaryTemplateHandler.onPublish ? (
                <PublishSettingButton
                  settingLabel="summary template"
                  hasChangesToPublish={isSummaryTemplateChanged}
                  isPublishBlocked={isPublishBlocked}
                  isPublishing={summaryTemplateHandler.isPublishing}
                  globalOnlyChanges={globalOnlyChanges}
                  onPublish={summaryTemplateHandler.onPublish}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </AssessmentEditorSplitLayout>
  );
};
